import { useCallback, useEffect, useState } from 'react';
import { api, ErreurApi, type GrapheFiscal, type ResolutionFiscale, type SyntheseFiscale } from './api';

/**
 * §13 — MOWIBO FISCAL : LE PROPERTY-FISCAL GRAPH.
 *
 * ⚠️ CET ÉCRAN A UNE SEULE OBLIGATION QUI PRIME TOUTES LES AUTRES : ne jamais
 * laisser lire « aucune taxe due » là où la vérité est « le fisc n'a pas
 * répondu ». Sur un tableau vide, les deux se ressemblent. Le premier veut dire
 * qu'on ne sait pas ; le second, qu'on sait et que c'est zéro. Un propriétaire
 * qui lit le second alors que c'était le premier ne paie pas, et se croit
 * en règle.
 *
 * Toutes les phrases d'état viennent du SERVICE. L'écran ne les compose pas :
 * une règle recopiée dans un composant se perd, et c'est toujours la phrase
 * dangereuse qui survit.
 *
 * ⚠️ ET LE CADASTRE NE CALCULE AUCUN IMPÔT. Valeur, assiette, taux, échéances
 * et paiements appartiennent à Mowibo Fiscal. L'écran DIT ce qu'il ne détient
 * pas, plutôt que de laisser des cases vides que le lecteur prendrait pour
 * l'absence de dette.
 */
export function Fiscal({ onOuvrirParcelle }: { onOuvrirParcelle: (id: string) => void }) {
  const [synthese, setSynthese] = useState<SyntheseFiscale | null>(null);
  const [parcelle, setParcelle] = useState('');
  const [graphe, setGraphe] = useState<GrapheFiscal | null>(null);
  const [refFiscale, setRefFiscale] = useState('');
  const [resolution, setResolution] = useState<ResolutionFiscale | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);

  const charger = useCallback(async () => {
    setChargement(true); setErreur(null);
    try { setSynthese(await api.fiscal.synthese()); }
    catch (e) { setErreur(e instanceof ErreurApi ? e.message : String((e as Error)?.message ?? e)); }
    finally { setChargement(false); }
  }, []);
  useEffect(() => { void charger(); }, [charger]);

  const ouvrirParcelle = async (ident: string) => {
    setErreur(null); setResolution(null);
    try { setGraphe(await api.fiscal.parcelle(ident)); }
    catch (e) { setGraphe(null); setErreur(e instanceof ErreurApi ? e.message : String((e as Error)?.message ?? e)); }
  };

  const resoudre = async () => {
    setErreur(null); setGraphe(null);
    const q = new URLSearchParams();
    // Un UUID est un lien ÉTABLI ; toute autre chaîne est une référence
    // DÉCLARÉE. Le service dit lequel des deux chemins a mené au résultat.
    if (/^[0-9a-f-]{36}$/i.test(refFiscale.trim())) q.set('fiscalPropertyId', refFiscale.trim());
    else q.set('ref', refFiscale.trim());
    try { setResolution(await api.fiscal.resoudre(q)); }
    catch (e) {
      // ⚠️ MÊME PIÈGE QUE §16, ET LE DISCRIMINANT EST LE MÊME : « rien ne
      // correspond » arrive en 404 AVEC un corps utile — c'est un RÉSULTAT,
      // pas une panne. Le laisser remonter comme erreur afficherait
      // « le service est en défaut » à un agent qui cherche simplement une
      // référence inconnue.
      //
      // ⚠️ ET L'INVERSE EST PIRE. Accepter TOUT 404 ferait passer une route mal
      // orthographiée pour une réponse métier — un verdict fabriqué par une
      // faute de frappe. Le discriminant est la PRÉSENCE du champ `trouve`,
      // jamais le code HTTP. C'était `corps` dans ma première version : le
      // champ s'appelle `corps404`, et la faute aurait fait tomber TOUS les
      // « rien ne correspond » dans la branche d'erreur.
      if (e instanceof ErreurApi && e.statut === 404 && e.corps404 && 'trouve' in e.corps404) {
        setResolution(e.corps404 as unknown as ResolutionFiscale);
        return;
      }
      setErreur(e instanceof ErreurApi ? e.message : String((e as Error)?.message ?? e));
    }
  };

  return (
    <div className="tableau-bord">
      <div className="entete-scene">
        <h2>💰 Fiscal</h2>
        <p className="mention">
          Le cadastre constate un fait matériel — la parcelle existe, sa surface mesurée vaut tant.
          Il ne calcule aucun impôt. Ce qui suit dit ce qui est rapproché, ce qui ne l'est pas,
          et ce que le registre ne détient pas.
        </p>
      </div>

      {erreur && <p className="erreur">{erreur}</p>}
      {chargement && <p className="attente">Mesure de la couverture…</p>}

      {synthese && (
        <>
          {/* ⚠️ L'ÉTAT DU SERVICE EN TÊTE, TOUJOURS. Sans lui, tous les
              « non vérifiable » ci-dessous se liraient comme des anomalies du
              registre plutôt que comme le silence du fisc. */}
          <p className={synthese.service_fiscal.joignable ? 'rien-a-signaler' : 'indetermine'}>
            {synthese.service_fiscal.joignable ? '● ' : '⚠️ '}{synthese.service_fiscal.lecture}
          </p>

          <h3 className="titre-section">Couverture</h3>
          <table className="tableau">
            <tbody>
              <tr><th>Parcelles actives dans votre portée</th><td>{synthese.parcelles_actives.toLocaleString('fr-FR')}</td></tr>
              <tr><th>Rattachées à un objet fiscal</th><td>{synthese.parcelles_liees.toLocaleString('fr-FR')}</td></tr>
              <tr><th>Taux de couverture</th>
                  <td>{synthese.couverture.taux_pct === null
                        ? <span className="etiquette">indéterminé</span>
                        : `${synthese.couverture.taux_pct} %`}</td></tr>
              <tr><th>Rapprochements incohérents</th><td>{synthese.liens_incoherents}</td></tr>
              <tr><th>Écarts de surface &gt; 10 %</th><td>{synthese.ecarts_superieurs_10pct}</td></tr>
            </tbody>
          </table>
          <p className="mention">{synthese.couverture.lecture}</p>
          <p className={synthese.liens_non_verifiables > 0 ? 'indetermine' : 'mention'}>
            {synthese.lecture_non_verifiables}
          </p>
        </>
      )}

      {/* ── Cadastral → Fiscal ─────────────────────────────────────────── */}
      <h3 className="titre-section">Ouvrir une parcelle</h3>
      <form className="filtres" onSubmit={(e) => { e.preventDefault(); if (parcelle.trim()) void ouvrirParcelle(parcelle.trim()); }}>
        <label>Identifiant cadastral
          <input value={parcelle} onChange={(e) => setParcelle(e.target.value)} placeholder="GN-PAR-…" />
        </label>
        <button className="bouton">Voir la fiscalité</button>
      </form>

      {graphe && <Graphe g={graphe} onOuvrirParcelle={onOuvrirParcelle} />}

      {/* ── Fiscal → Cadastral Map ─────────────────────────────────────── */}
      <h3 className="titre-section">Retrouver une parcelle depuis une référence fiscale</h3>
      <p className="mention">
        La navigation retour du §13. Un identifiant d'objet fiscal passe par le rapprochement
        VÉRIFIÉ ; toute autre chaîne est une référence déclarée hors du cadastre, et n'engage
        que celui qui l'a saisie.
      </p>
      <form className="filtres" onSubmit={(e) => { e.preventDefault(); if (refFiscale.trim()) void resoudre(); }}>
        <label>Référence fiscale ou cadastrale
          <input value={refFiscale} onChange={(e) => setRefFiscale(e.target.value)} placeholder="UUID fiscal, ou GN-PAR-…" />
        </label>
        <button className="bouton">Résoudre</button>
      </form>

      {resolution && (
        resolution.trouve ? (
          <div className={resolution.fiabilite === 'verifiee' ? 'rien-a-signaler' : 'indetermine'}>
            <p>
              <strong>{resolution.parcelle}</strong> — statut {resolution.statut_parcelle}
              {' · '}voie {resolution.voie === 'lien_etabli' ? 'du rapprochement vérifié' : 'de la référence déclarée'}
            </p>
            {resolution.avertissement && <p>{resolution.avertissement}</p>}
            <button className="bouton" onClick={() => onOuvrirParcelle(resolution.parcelle!)}>
              Ouvrir sur la carte
            </button>
          </div>
        ) : <p className="indetermine">{resolution.lecture}</p>
      )}
    </div>
  );
}

function Graphe({ g, onOuvrirParcelle }: { g: GrapheFiscal; onOuvrirParcelle: (id: string) => void }) {
  return (
    <>
      <div className="entete-fiche">
        <div>
          <h3><button className="lien" onClick={() => onOuvrirParcelle(g.parcelle.identifiant)}>{g.parcelle.identifiant}</button></h3>
          <p className="mention">
            Surface mesurée {g.parcelle.surface_mesuree_m2 === null ? 'inconnue' : `${g.parcelle.surface_mesuree_m2.toLocaleString('fr-FR')} m²`}
            {' · '}statut {g.parcelle.statut}
          </p>
        </div>
      </div>

      {g.rattachement_fiscal.etat === 'aucun_lien' ? (
        <p className="indetermine">⚠️ {g.rattachement_fiscal.lecture}</p>
      ) : (
        g.objets_fiscaux.map((o) => (
          <div key={o.fiscal_property_id}
               className={o.statut === 'verifie' ? 'rien-a-signaler' : o.statut === 'incoherent' ? 'avertissement' : 'indetermine'}>
            <p><code>{o.fiscal_property_id}</code>{o.juridiction && ` · juridiction ${o.juridiction}`}</p>
            {/* La phrase du service, telle quelle. */}
            <p>{o.lecture}</p>
            <p className="mention">
              Surface constatée au cadastre : {o.surface_constatee_m2?.toLocaleString('fr-FR') ?? '—'} m²
              {o.surface_declaree_m2 !== undefined
                ? ` · déclarée au fisc : ${o.surface_declaree_m2?.toLocaleString('fr-FR') ?? '—'} m²`
                : ' · surface déclarée au fisc : masquée par votre habilitation'}
              {o.ecart_surface_pct !== null && ` · écart ${o.ecart_surface_pct} %`}
            </p>
          </div>
        ))
      )}

      <h4 className="titre-section">Le graphe — parcelle ↔ bâtiment ↔ bien ↔ copropriété</h4>
      <table className="tableau">
        <tbody>
          <tr><th>Emprises bâties</th>
              <td>{g.graphe.batiments.length === 0 ? 'aucune emprise rattachée' :
                   g.graphe.batiments.map((b) => b.identifiant).join(', ')}</td></tr>
          <tr><th>Annonces immobilières</th>
              <td>{g.graphe.biens_immobiliers.length === 0 ? 'aucune annonce rapprochée' :
                   g.graphe.biens_immobiliers.map((b) => `${b.bien_id.slice(0, 8)}… (${b.statut})`).join(', ')}</td></tr>
          <tr><th>Copropriétés</th>
              <td>{g.graphe.coproprietes.length === 0 ? 'aucune' :
                   g.graphe.coproprietes.map((c) => `${c.identifiant} — ${c.denomination}`).join(', ')}</td></tr>
          <tr><th>Titulaires</th>
              <td>{g.graphe.titulaires === undefined
                    ? <span className="masque">masqué par votre habilitation — ce n'est pas « aucun titulaire »</span>
                    : g.graphe.titulaires.length === 0 ? 'aucun droit actif inscrit'
                    : g.graphe.titulaires.map((t) => `${t.denomination} (${t.quotite_num}/${t.quotite_den}${t.taxpayer_id ? ', contribuable connu' : ', non rattaché au fisc'})`).join(' · ')}</td></tr>
        </tbody>
      </table>

      {/* ⚠️ CE QUE LE CADASTRE NE DÉTIENT PAS, DIT PLUTÔT QUE LAISSÉ VIDE. */}
      <h4 className="titre-section">Ce que le cadastre ne détient pas</h4>
      <p className="mention">{g.non_detenu_par_le_cadastre.lecture}</p>
      <ul className="liste">
        {g.non_detenu_par_le_cadastre.champs.map((c) => <li key={c}>{c.replace(/_/g, ' ')}</li>)}
      </ul>
    </>
  );
}
